/**
 * Template de prise de besoin : Rapport de leçons apprises v1
 * Aligné sur ITIL 4, COBIT 2019 et ISO 27001
 * Document synthétique des leçons apprises
 */

import type { IntakeTemplate } from '../templateTypes';

export const templateRapportLeconsApprisesV1: IntakeTemplate = {
  id: 'rapport-lecons-apprises-v1',
  name: 'Prise de besoin Rapport de leçons apprises v1',
  description:
    'Questionnaire complet pour élaborer un Rapport de leçons apprises conforme ITIL 4, COBIT 2019 et ISO 27001. Documente les bonnes pratiques identifiées, les améliorations à apporter et les actions correctives.',
  documentTypeCode: 'LESSONS_LEARNED_REPORT',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Contexte & Identification',
      description: 'Informations sur l\'incident et l\'entreprise',
      icon: 'Building2',
      questions: [
        {
          id: 'entreprise',
          label: 'Entreprise',
          type: 'text',
          placeholder: 'Nom de l\'entreprise',
          required: true,
          helpText: 'Identifiant principal pour le rapport.',
        },
        {
          id: 'nom_incident',
          label: 'Nom de l\'incident',
          type: 'text',
          placeholder: 'Ex: Incident de sécurité - Fuite de données',
          required: true,
          helpText: 'Nom descriptif de l\'incident pour identification.',
        },
        {
          id: 'date_incident',
          label: 'Date de l\'incident',
          type: 'date',
          required: true,
          helpText: 'Date de survenue de l\'incident.',
        },
        {
          id: 'type_incident',
          label: 'Type d\'incident',
          type: 'select',
          options: [
            'Incident de sécurité',
            'Incident de disponibilité',
            'Incident d\'intégrité',
            'Incident de confidentialité',
            'Incident majeur',
            'Autre',
          ],
          required: true,
          helpText: 'ITIL 4: Classification du type d\'incident.',
        },
        {
          id: 'ton_document',
          label: 'Quel ton souhaitez-vous pour ce rapport ?',
          type: 'select',
          options: [
            'Formel et synthétique (ton professionnel, synthèse claire)',
            'Pédagogique et explicatif (ton guide pratique)',
            'Mixte (équilibre entre formel et pédagogique)',
          ],
          helpText: 'Le ton influence la facilité de compréhension du rapport.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Ce qui a Bien Fonctionné',
      description: 'Bonnes pratiques et succès',
      icon: 'CheckCircle',
      questions: [
        {
          id: 'succes_principaux',
          label: 'Quels sont les principaux succès de la gestion de l\'incident ?',
          type: 'textarea',
          placeholder: 'Décrivez les succès: détection rapide, réponse efficace, communication claire...',
          required: true,
          helpText: 'ITIL 4: Succès pour identification des bonnes pratiques.',
        },
        {
          id: 'bonnes_pratiques',
          label: 'Quelles bonnes pratiques ont été identifiées ?',
          type: 'textarea',
          placeholder: 'Listez les bonnes pratiques à reproduire: processus, outils, méthodes...',
          required: true,
          helpText: 'ITIL 4: Bonnes pratiques pour standardisation.',
        },
        {
          id: 'forces_equipe',
          label: 'Quelles sont les forces de l\'équipe qui ont contribué au succès ?',
          type: 'textarea',
          placeholder: 'Décrivez les forces: compétences, collaboration, réactivité...',
          helpText: 'ITIL 4: Forces pour valorisation et reproduction.',
        },
        {
          id: 'outils_efficaces',
          label: 'Quels outils ou processus ont été particulièrement efficaces ?',
          type: 'textarea',
          placeholder: 'Décrivez les outils efficaces: SIEM, procédures, workflows...',
          helpText: 'ITIL 4: Outils efficaces pour standardisation.',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Ce qui Doit Être Amélioré',
      description: 'Points d\'amélioration identifiés',
      icon: 'AlertTriangle',
      questions: [
        {
          id: 'points_amelioration',
          label: 'Quels sont les principaux points d\'amélioration identifiés ?',
          type: 'textarea',
          placeholder: 'Décrivez les points d\'amélioration: processus, outils, formation, documentation...',
          required: true,
          helpText: 'ITIL 4: Points d\'amélioration pour plan d\'action.',
        },
        {
          id: 'difficultes_rencontrees',
          label: 'Quelles difficultés ont été rencontrées ?',
          type: 'textarea',
          placeholder: 'Décrivez les difficultés: manque d\'outils, manque d\'information, coordination, délais...',
          required: true,
          helpText: 'ITIL 4: Difficultés pour identification des améliorations.',
        },
        {
          id: 'erreurs_commises',
          label: 'Quelles erreurs ont été commises et comment les éviter ?',
          type: 'textarea',
          placeholder: 'Décrivez les erreurs et les moyens de les éviter...',
          helpText: 'ITIL 4: Erreurs pour prévention des récurrences.',
        },
        {
          id: 'faiblesses_processus',
          label: 'Quelles sont les faiblesses des processus actuels ?',
          type: 'textarea',
          placeholder: 'Décrivez les faiblesses: manque de clarté, délais trop longs, responsabilités floues...',
          helpText: 'ITIL 4: Faiblesses pour amélioration des processus.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Leçons Apprises',
      description: 'Synthèse des leçons apprises',
      icon: 'TrendingUp',
      questions: [
        {
          id: 'lecons_apprises',
          label: 'Quelles sont les principales leçons apprises ?',
          type: 'textarea',
          placeholder: 'Synthétisez les leçons apprises: ce qui doit être retenu, ce qui doit être changé...',
          required: true,
          helpText: 'ITIL 4: Leçons apprises pour amélioration continue.',
        },
        {
          id: 'insights_cles',
          label: 'Quels sont les insights clés à retenir ?',
          type: 'textarea',
          placeholder: 'Décrivez les insights: découvertes importantes, réalités révélées...',
          helpText: 'ITIL 4: Insights pour compréhension approfondie.',
        },
        {
          id: 'changements_recommandes',
          label: 'Quels changements sont recommandés ?',
          type: 'textarea',
          placeholder: 'Décrivez les changements: processus, outils, formation, organisation...',
          required: true,
          helpText: 'ITIL 4: Changements pour plan d\'amélioration.',
        },
      ],
    },
    {
      id: 'phase5',
      title: 'Actions Correctives & Suivi',
      description: 'Plan d\'action et suivi',
      icon: 'FileCheck',
      questions: [
        {
          id: 'actions_correctives',
          label: 'Quelles actions correctives doivent être mises en place ?',
          type: 'textarea',
          placeholder: 'Listez les actions: corrections immédiates, préventions, formations, améliorations...',
          required: true,
          helpText: 'ITIL 4: Actions correctives pour prévention des récurrences.',
        },
        {
          id: 'priorites_actions',
          label: 'Quelles sont les priorités des actions ?',
          type: 'textarea',
          placeholder: 'Définissez les priorités: urgent, important, à planifier...',
          helpText: 'ITIL 4: Priorités pour planification efficace.',
        },
        {
          id: 'responsables_actions',
          label: 'Qui sont les responsables des actions correctives ?',
          type: 'textarea',
          placeholder: 'Décrivez les responsables: rôles, responsabilités, délais...',
          required: true,
          helpText: 'ITIL 4: Responsables pour garantie d\'implémentation.',
        },
        {
          id: 'suivi_ameliorations',
          label: 'Comment sera suivi le plan d\'amélioration ?',
          type: 'textarea',
          placeholder: 'Décrivez le suivi: indicateurs, révisions, rapports...',
          helpText: 'ITIL 4: Suivi pour garantie de l\'implémentation des améliorations.',
        },
      ],
    },
  ],
};





